import React, { useState, useEffect, useRef } from 'react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface VideoPlayerProps {
  className?: string;
  style?: React.CSSProperties;
}

export default function VideoPlayer({ className = '', style = {} }: VideoPlayerProps) {
  const prefersReducedMotion = useReducedMotion();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [autoplayFailed, setAutoplayFailed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ensure correct base path in production (e.g., when the app is served from a subpath)
  const baseUrl = (import.meta as any).env?.BASE_URL || '/';

  // Video sources in order of preference
  const videoSources = [
    { src: `${baseUrl}hero-video.mp4`, type: 'video/mp4' }
  ];

  // Debug function
  const addDebugInfo = (message: string) => {
    console.log('VideoPlayer:', message);
    setDebugInfo(prev => [...prev.slice(-4), `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const attemptPlay = () => {
    if (!videoRef.current) return;
    videoRef.current.play()
      .then(() => {
        setAutoplayFailed(false);
        setIsPlaying(true);
        addDebugInfo('Playback started');
      })
      .catch(error => {
        setAutoplayFailed(true);
        addDebugInfo(`Playback blocked: ${error.message}`);
      });
  };

  useEffect(() => {
    addDebugInfo('VideoPlayer component mounted');
    addDebugInfo(`Reduced motion preference: ${prefersReducedMotion}`);
    addDebugInfo(`BASE_URL resolved: ${baseUrl}`);
    
    // Check if video files exist
    videoSources.forEach(source => {
      fetch(source.src, { method: 'HEAD' })
        .then(response => {
          addDebugInfo(`${source.src} - Status: ${response.status} ${response.ok ? 'OK' : 'FAILED'}`);
        })
        .catch(error => {
          addDebugInfo(`${source.src} - FETCH ERROR: ${error.message}`);
        });
    });

    // Always try to load video after short delay (even with reduced motion for debugging)
    const timer = setTimeout(() => {
      addDebugInfo('Setting shouldLoadVideo to true');
      setShouldLoadVideo(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [prefersReducedMotion]);

  // Ensure the browser starts loading data even when autoplay is disabled
  useEffect(() => {
    if (shouldLoadVideo && videoRef.current) {
      try {
        videoRef.current.load();
      } catch (e) {
        // no-op
      }
    }
  }, [shouldLoadVideo]);

  const handleVideoLoad = () => {
    addDebugInfo('Video onLoadedData event fired');
    setVideoLoaded(true);
    setVideoError(false);
  };

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const target = e.target as HTMLVideoElement;
    const error = target.error;
    addDebugInfo(`Video error - Code: ${error?.code}, Message: ${error?.message || 'Unknown error'}`);
    addDebugInfo(`Current src: ${target.currentSrc || target.src}`);
    setVideoError(true);
    
    // Don't throw error - handle gracefully by falling back to background
    console.warn('Video loading failed, falling back to static background');
  };

  const handleCanPlay = () => {
    addDebugInfo('Video canPlay event fired');
    if (!prefersReducedMotion) {
      attemptPlay();
    } else {
      addDebugInfo('Skipping autoplay due to reduced motion preference');
      setAutoplayFailed(true); // allow manual play button
    }
  };

  const handleLoadStart = () => {
    addDebugInfo('Video loadStart event fired');
  };

  const handleLoadedMetadata = () => {
    addDebugInfo('Video loadedMetadata event fired');
    if (videoRef.current) {
      addDebugInfo(`Video dimensions: ${videoRef.current.videoWidth}x${videoRef.current.videoHeight}`);
      addDebugInfo(`Video duration: ${videoRef.current.duration}s`);
    }
  };

  const handleProgress = () => {
    if (videoRef.current) {
      const buffered = videoRef.current.buffered;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        addDebugInfo(`Video buffered: ${bufferedEnd.toFixed(2)}s`);
      }
    }
  };

  // Show debug info and render video even with reduced motion for debugging
  const shouldRenderVideo = shouldLoadVideo && !videoError;

  return (
    <div className={`${className} relative`}>
      {/* Debug information overlay - Disabled */}
      {false && import.meta.env.DEV && (
        <div className="absolute top-4 left-4 bg-black/80 text-white p-2 rounded text-xs max-w-xs z-50">
          <div className="font-bold mb-1">Video Debug Info:</div>
          {debugInfo.map((info, index) => (
            <div key={index} className="text-xs">{info}</div>
          ))}
          <div className="mt-2 text-yellow-400">
            shouldLoadVideo: {shouldLoadVideo.toString()}<br/>
            videoLoaded: {videoLoaded.toString()}<br/>
            videoError: {videoError.toString()}<br/>
            reducedMotion: {prefersReducedMotion.toString()}
          </div>
        </div>
      )}

      {shouldRenderVideo ? (
        <video
          ref={videoRef}
          autoPlay={!prefersReducedMotion}
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
          style={style}
          onLoadedData={handleVideoLoad}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleVideoError}
          onCanPlay={handleCanPlay}
          onLoadStart={handleLoadStart}
          onProgress={handleProgress}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          controls={import.meta.env.DEV}
        >
          {videoSources.map((source, index) => (
            <source 
              key={index}
              src={source.src} 
              type={source.type}
            />
          ))}
          Your browser does not support the video tag.
        </video>
      ) : (
        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="text-lg mb-2">🎬</div>
            <div className="text-sm">
              {videoError ? 'Video failed to load' : 'Loading video...'}
            </div>
            {import.meta.env.DEV && (
              <div className="text-xs mt-2 text-gray-400">
                Check console for details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fallback control if autoplay is blocked or reduced-motion is enabled */}
      {shouldRenderVideo && (autoplayFailed || (!isPlaying && prefersReducedMotion)) && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <button
            className="pointer-events-auto rounded-full bg-black/70 text-white px-5 py-3 border border-white/20 hover:bg-black/80 transition"
            onClick={(e) => {
              e.stopPropagation();
              attemptPlay();
            }}
          >
            Play background video
          </button>
        </div>
      )}
    </div>
  );
}
